import hexRgb from 'hex-rgb';
import type { ColorKeys } from '@allenai/varnish-theme/mui';
import varnishTheme from '@allenai/varnish-theme/mui';

import { getContrastText } from './theme-utils';

type VarnishThemeColor = (typeof varnishTheme)['color'][ColorKeys];

export class RGBA {
    constructor(
        public r: number,
        public g: number,
        public b: number,
        public a: number
    ) {}

    toString() {
        return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
    }
}

// convert a hex color string to a RGBA
export function hexToRgb(hex: string): RGBA {
    const rgba = hexRgb(hex);
    return new RGBA(rgba.red, rgba.green, rgba.blue, rgba.alpha);
}

export class Color {
    public readonly displayName: string;
    public readonly hex: string;
    public readonly rgba: RGBA;

    public readonly useContrastText: boolean;
    public readonly contrastTextColor: string;

    constructor(displayName: string, hex: string, rgba?: RGBA, useContrastText?: boolean) {
        this.displayName = displayName;
        this.hex = hex.toUpperCase();
        this.rgba = rgba || hexToRgb(hex);
        this.useContrastText = useContrastText ?? false;
        this.contrastTextColor = getContrastText(this.hex);
    }

    toString() {
        return this.hex;
    }
}

// get color from style token
const getColor = (color: VarnishThemeColor) => {
    const c = color.attributes;
    const rgba = new RGBA(c.rgb.r, c.rgb.g, c.rgb.b, c.rgb.a);
    const useContrastText = 'useContrastText' in c ? c.useContrastText : false;

    // Include alpha in hex when it's not fully opaque
    const alphaHex =
        c.rgb.a < 1
            ? Math.round(c.rgb.a * 255)
                  .toString(16)
                  .padStart(2, '0')
            : '';
    const hex = `#${c.hex.toUpperCase()}${alphaHex.toUpperCase()}`;

    const colorName = color.name.replace('color-', '');
    // Make color names formatted like B10 or R2 uppercase
    const displayName = colorName.match(/^\D\d+$/) ? colorName.toUpperCase() : colorName;
    return new Color(displayName, hex, rgba, useContrastText);
};

// colors grabbed from varnish-theme
export const color = Object.entries(varnishTheme.color).reduce(
    (acc, [k, v]) => {
        acc[k as ColorKeys] = getColor(v);
        return acc;
    },
    {} as Record<ColorKeys, Color>
);

// TODO: these should pull from VarnishTheme. This is not currently done because the VarnishTheme
// does not expose hex nor a color id like 'R10'. And converting from string rgb is not ideal.
// This will likely be reworked in the future due to new branding. (very likely, we wont expose
// charting colors from the mui theme at all)
export const lightCategoricalColor = {
    Red: new Color('Red', color.R10.hex, color.R10.rgba, color.R10.useContrastText),
    Orange: new Color('Orange', color.O9.hex, color.O9.rgba, color.O9.useContrastText),
    Aqua: new Color('Aqua', color.A10.hex, color.A10.rgba, color.A10.useContrastText),
    Teal: new Color('Teal', color.T8.hex, color.T8.rgba, color.T8.useContrastText),
    Blue: new Color('Blue', color.B6.hex, color.B6.rgba, color.B6.useContrastText),
    Magenta: new Color('Magenta', color.M10.hex, color.M10.rgba, color.M10.useContrastText),
    Purple: new Color('Purple', color.P8.hex, color.P8.rgba, color.P8.useContrastText),
    Green: new Color('Green', color.G10.hex, color.G10.rgba, color.G10.useContrastText),
} as const satisfies Record<string, Color>;

export const darkCategoricalColor = {
    Red: new Color('Red', color.R4.hex, color.R4.rgba, color.R4.useContrastText),
    Orange: new Color('Orange', color.O4.hex, color.O4.rgba, color.O4.useContrastText),
    Aqua: new Color('Aqua', color.A3.hex, color.A3.rgba, color.A3.useContrastText),
    Teal: new Color('Teal', color.T3.hex, color.T3.rgba, color.T3.useContrastText),
    Blue: new Color('Blue', color.B3.hex, color.B3.rgba, color.B3.useContrastText),
    Magenta: new Color('Magenta', color.M4.hex, color.M4.rgba, color.M4.useContrastText),
    Purple: new Color('Purple', color.P4.hex, color.P4.rgba, color.P4.useContrastText),
    Green: new Color('Green', color.G4.hex, color.G4.rgba, color.G4.useContrastText),
} as const satisfies Record<string, Color>;

// use to convert a set of color ids top their hex values
// e.g. SequenceColors = ColorsIdsToHexVals(['N1', 'N3', 'N5', 'N7', 'N9']);
export const colorsIdsToHexVals = (colorIds: Array<keyof typeof color>) =>
    colorIds.map((k) => color[k].hex);
export const colorMapToHexVals = (
    colorMap: typeof darkCategoricalColor | typeof lightCategoricalColor | typeof color
) => Object.values(colorMap).map((v) => v.hex);
